/* sliders.js — seamless infinite sliders (#portfolio-slider, #reviews-slider)
 *
 * The loop wraps on the exact width of one set of items, measured from the DOM.
 * The previous version wrapped on scrollWidth/2, which also counted the track's
 * full-bleed side padding (180px a side at 1200+), so every loop jumped ~170px.
 */
(function () {
  function setupSlider(trackId, prevId, nextId, dotsId, speed) {
    var track = document.getElementById(trackId);
    if (!track) return;

    var prev = document.getElementById(prevId);
    var next = document.getElementById(nextId);
    var dots = dotsId ? document.getElementById(dotsId) : null;

    var originals = [].slice.call(track.children);
    var count = originals.length;
    if (!count) return;

    var calm = window.matchMedia('(prefers-reduced-motion: reduce)');
    var cycle = 0;          // exact width of one full set, in scroll px
    var pages = 1;
    var timer = null;
    var target = null;      // glide destination while an arrow/dot is animating
    var idle = 0;           // ticks to hold before the creep resumes
    var down = false, moved = false, startX = 0, startLeft = 0;

    /* RTL: the spec puts scrollLeft at 0 at the start edge and drives it
       negative as you scroll away, so every read/write below goes through these
       and the loop arithmetic stays in one positive coordinate space.

       isRTL used to call getComputedStyle on every write. setSx runs from the
       33 Hz creep tick AND from the scroll handler it triggers, so that was a
       forced style recalc several times per tick, per slider, forever. Lighthouse
       attributed 108 ms of forced reflow to sliders.js on mobile and 5,113 ms of
       CPU overall. Direction cannot change between two frames of a marquee, so
       it is resolved once and refreshed on resize. */
    var rtl = getComputedStyle(track).direction === 'rtl';
    function sx() { return Math.abs(track.scrollLeft); }
    function setSx(v) { track.scrollLeft = rtl ? -v : v; }
    function addSx(d) { setSx(sx() + d); }

    var TICK = 30;
    var HOLD = Math.round(4500 / TICK);   // ~4.5s pause after an interaction

    /* Cached measurements. sync() runs every tick and walks
       stride() -> perView() -> itemWidth() + gapWidth(), which was one
       getBoundingClientRect plus one getComputedStyle per tick on top of the
       above. None of these change unless the box changes, so they are measured
       lazily and invalidated on resize and on rebuild. */
    var mGap = null, mItem = null, mFrame = null;
    function invalidateMeasures() { mGap = mItem = mFrame = null; }

    function gapWidth() {
      if (mGap === null) {
        var cs = getComputedStyle(track);
        var g = parseFloat(cs.columnGap === 'normal' ? cs.gap : cs.columnGap);
        mGap = isNaN(g) ? 0 : g;
      }
      return mGap;
    }

    function itemWidth() {
      if (mItem === null) {
        mItem = track.children.length ? track.children[0].getBoundingClientRect().width : 0;
      }
      return mItem;
    }

    /* The tracks are full-bleed, so clientWidth is the viewport rather than the
       run of items on screen. Measure the container the heading sits in. */
    function frameWidth() {
      if (mFrame === null) {
        var c = track.closest('.container');
        mFrame = c ? c.clientWidth : track.clientWidth;
      }
      return mFrame;
    }

    function perView() {
      var w = itemWidth();
      if (!w) return 1;
      var g = gapWidth();
      return Math.max(1, Math.floor((frameWidth() + g) / (w + g)));
    }

    function stride() {
      return perView() * (itemWidth() + gapWidth());
    }

    function appendSet() {
      originals.forEach(function (node) {
        var clone = node.cloneNode(true);
        clone.setAttribute('data-clone', '1');
        clone.setAttribute('aria-hidden', 'true');
        clone.setAttribute('tabindex', '-1');
        // Links and buttons inside a hidden copy must not be reachable with Tab either.
        Array.prototype.forEach.call(clone.querySelectorAll('a[href], button, input, select, textarea, iframe, summary, [tabindex]'), function (n) {
          n.setAttribute('tabindex', '-1');
        });
        track.appendChild(clone);
      });
    }

    /* Rebuild the clone sets and re-measure. Enough sets are appended that the
       scroller can always sit anywhere in [0, 2*cycle) with a screen to spare. */
    function rebuild() {
      invalidateMeasures();
      rtl = getComputedStyle(track).direction === 'rtl';

      var kids = [].slice.call(track.children);
      for (var i = kids.length - 1; i >= 0; i--) {
        if (kids[i].getAttribute('data-clone') === '1') track.removeChild(kids[i]);
      }

      appendSet();
      // exact: the distance from the first original to its first copy
      cycle = Math.abs(track.children[count].offsetLeft - track.children[0].offsetLeft);

      if (cycle > 0) {
        var have = cycle * 2;
        var need = cycle * 2 + track.clientWidth;
        while (have < need) { appendSet(); have += cycle; }
      }

      setSx(0);
      buildDots();
      sync();
    }

    /* Every set is an identical copy, so subtracting one cycle is invisible.
       The band is [0, 2*cycle) rather than [0, cycle): the wider band leaves a
       full cycle of slack behind the current position, so stepping backwards
       from the start has somewhere to land and a smooth scroll crossing the
       boundary is not yanked mid-animation. */
    function loop() {
      if (cycle > 0 && sx() >= cycle * 2) {
        addSx(-cycle);
        // a glide in progress is aiming at an absolute position, so move its
        // destination by the same cycle or it would scroll back on itself
        if (target !== null) target -= cycle;
      }
    }

    function buildDots() {
      if (!dots) return;
      pages = Math.max(1, Math.ceil(count / perView()));
      dots.innerHTML = '';
      if (pages < 2) return;
      for (var i = 0; i < pages; i++) {
        var d = document.createElement('button');
        d.type = 'button';
        d.className = 'slider-dot' + (i === 0 ? ' active' : '');
        d.setAttribute('data-i', i);
        d.setAttribute('aria-label', 'Go to slide ' + (i + 1));
        d.addEventListener('click', function (e) {
          var idx = parseInt(e.currentTarget.getAttribute('data-i'), 10);
          // stay in whichever copy we are in, so a dot never scrolls the whole loop
          var base = cycle > 0 ? Math.floor(sx() / cycle) * cycle : 0;
          glideTo(base + idx * stride());
        });
        dots.appendChild(d);
      }
    }

    function sync() {
      if (!dots || !dots.children.length || cycle <= 0) return;
      var s = stride();
      var cur = s ? Math.round((sx() % cycle) / s) % pages : 0;
      for (var i = 0; i < dots.children.length; i++) {
        dots.children[i].classList.toggle('active', i === cur);
      }
    }

    /* Glide to an absolute position. Deliberately NOT scrollTo({behavior:
       'smooth'}): the creep below writes scrollLeft every tick, and any write
       cancels a native smooth scroll, so arrow clicks were being killed ~30ms
       in. Driving both from one tick removes the conflict entirely. */
    function glideTo(to) {
      if (calm.matches) { setSx(to); loop(); sync(); return; }
      target = to;
    }

    function step(dir) {
      var amount = Math.max(itemWidth() + gapWidth(), track.clientWidth * 0.6);
      var from = target !== null ? target : sx();
      // stepping back from the start: hop forward a cycle first so there is
      // somewhere to go; the copy makes the hop invisible
      if (dir < 0 && from < amount) {
        addSx(cycle);
        from += cycle;
      }
      glideTo(from + dir * amount);
    }

    function tick() {
      if (down) return;                       // mid-drag

      if (target !== null) {
        var d = target - sx();
        if (Math.abs(d) < 1.5) {
          setSx(target);
          target = null;
          idle = HOLD;
        } else {
          addSx(d * 0.16);                    // ease out
        }
        loop();
        sync();
        return;
      }

      if (idle > 0) { idle--; return; }
      if (hovered) return;

      addSx(speed);
      loop();
    }

    /* track.matches(':hover') forced a style recalc on every tick. Two listeners
       carry the same state for free. */
    var hovered = false;
    track.addEventListener('mouseenter', function () { hovered = true; });
    track.addEventListener('mouseleave', function () { hovered = false; });

    /* The creep used to run at 33 Hz for the life of the page whether or not the
       slider was anywhere near the viewport — and every page carries one. It now
       runs only while on screen and while the tab is visible. */
    var onScreen = true, canRun = true;
    function refreshAuto() {
      if (onScreen && canRun && !document.hidden) startAuto(); else stopAuto();
    }

    function startAuto() {
      if (timer || calm.matches) return;
      timer = setInterval(tick, TICK);
    }

    function stopAuto() {
      if (timer) { clearInterval(timer); timer = null; }
    }

    if (prev) prev.addEventListener('click', function () { step(-1); });
    if (next) next.addEventListener('click', function () { step(1); });

    /* Run straight off the scroll event. Deferring to requestAnimationFrame and
       guarding with a stored frame id wedges permanently if that frame is ever
       dropped (a backgrounded tab does not run rAF): the id stays set and every
       later scroll returns early, so the loop silently stops wrapping. Both
       loop() and sync() are O(1)-ish, so there is nothing to throttle. */
    track.addEventListener('scroll', function () {
      loop();
      sync();
    });

    /* drag-to-scroll */
    track.addEventListener('mousedown', function (e) {
      if (e.button !== 0) return;
      down = true; moved = false; startX = e.pageX; startLeft = sx();
      target = null;          // abandon any glide, the pointer wins
    });
    window.addEventListener('mousemove', function (e) {
      if (!down) return;
      // in RTL the track runs the other way, so a rightward drag scrolls back
      var dx = (e.pageX - startX) * (rtl ? -1 : 1);
      if (Math.abs(dx) > 6) { moved = true; track.classList.add('dragging'); }
      if (moved) {
        e.preventDefault();
        var left = startLeft - dx;
        if (cycle > 0) {
          while (left < 0) left += cycle;
          while (left >= cycle * 2) left -= cycle;
        }
        setSx(left);
      }
    });
    window.addEventListener('mouseup', function () {
      if (!down) return;
      down = false;
      idle = HOLD;
      setTimeout(function () { track.classList.remove('dragging'); moved = false; }, 50);
    });
    track.addEventListener('click', function (e) {
      if (moved || track.classList.contains('dragging')) { e.preventDefault(); e.stopPropagation(); }
    }, true);

    var rt = null;
    window.addEventListener('resize', function () {
      invalidateMeasures();
      clearTimeout(rt);
      rt = setTimeout(rebuild, 200);
    });

    if ('IntersectionObserver' in window) {
      new IntersectionObserver(function (entries) {
        onScreen = entries[0].isIntersecting;
        refreshAuto();
      }, { threshold: 0 }).observe(track);
    }
    document.addEventListener('visibilitychange', refreshAuto);

    rebuild();
    refreshAuto();
  }

  /* Each slider is built only when it comes within ~600px of the viewport.
     Building clones the items and measures them, which is layout work; every
     slider sits well below the fold, so doing it during page load only added
     forced reflows, extra DOM nodes and main-thread time before first
     interaction. By the time a visitor scrolls to one it is already running. */
  function whenNear(id, fn) {
    var el = document.getElementById(id);
    if (!el) return;
    if (!('IntersectionObserver' in window)) { fn(); return; }
    var io = new IntersectionObserver(function (entries) {
      if (!entries[0].isIntersecting) return;
      io.disconnect();
      fn();
    }, { rootMargin: '600px 0px' });
    io.observe(el);
  }

  function init() {
    whenNear('ps-track', function () { setupSlider('ps-track', 'ps-prev', 'ps-next', 'ps-dots', 1); });
    whenNear('rs-track', function () { setupSlider('rs-track', 'rs-prev', 'rs-next', 'rs-dots', 0.8); });
    whenNear('pr-track', function () { setupSlider('pr-track', 'pr-prev', 'pr-next', 'pr-dots', 0.9); });

    /* Video review click handler. Delegated, because the slider's clones are now
       created after this runs; a direct listener would miss them. */
    document.addEventListener('click', function (e) {
      var v = e.target.closest ? e.target.closest('.rs-video') : null;
      if (!v) return;
      var parent = v.closest('.slider-track');
      if (parent && parent.classList.contains('dragging')) { e.preventDefault(); return; }
      var url = v.getAttribute('data-video-url');
      if (url && url !== '#') { window.open(url, '_blank'); }
    });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
